/**
 * TASK-069 — soffier.emovia.com dandelion physics.
 *
 * Pure simulation module: no DOM, no React, fully unit-testable.
 * Input: wind force + delta-time. Output: mutated seed state.
 *
 * Key design choices:
 * - Seeds placed via phyllotaxis (golden-angle Fibonacci spiral projected onto a
 *   sphere). Fully deterministic — same N always gives the same positions.
 * - Turbulence from a deterministic 2D noise (LCG per-seed + sine hash) keyed
 *   by seed index, so every seed drifts on its own arc without Math.random in
 *   the sim loop.
 * - Drag model: high air resistance (pappo parachute effect). Gravity is gentle.
 * - Pun-moment steering uses simple arrival behaviour (seek + slow-down zone).
 */

export type Vec2 = { x: number; y: number };

/** Life-cycle states of a single seed. */
export type SeedStatus = 'attached' | 'airborne' | 'forming' | 'gone';

export type Seed = {
  /** Normalised polar position on the sphere (theta, phi). */
  theta: number;
  phi: number;
  /** World-space position. */
  x: number;
  y: number;
  /** Velocity. */
  vx: number;
  vy: number;
  /** Rotation angle of the seed on screen (radians). */
  rot: number;
  rotSpeed: number;
  /** Current life-cycle state. */
  status: SeedStatus;
  /** Per-seed noise phase, derived deterministically from index. */
  noiseSeed: number;
  /** Index of assigned target point during forming/pun (−1 = free). */
  target: number;
  /** Brand-tint amount in [0,1] applied while EMOVIA letters settle. */
  tint: number;
  /** Accumulated time in the current state (seconds). */
  stateAge: number;
  /** Opacity, clamped [0,1]. */
  alpha: number;
};

export type DandelionConfig = {
  /** Total seeds to generate (80–150 recommended). */
  count: number;
  /** World-space centre of the capolino. */
  cx: number;
  cy: number;
  /** Sphere radius in px. */
  radius: number;
};

export type WindState = {
  /** Current wind intensity (0 = no wind). Grows on hold, decays on release. */
  strength: number;
  /** Direction unit vector (normalised). */
  dx: number;
  dy: number;
};

/** Scene-level state machine for the pun moment. */
export type ScenePhase =
  | 'idle'
  | 'blowing'
  | 'pun-forming'   // steering to SOFFIEREMO VIA
  | 'pun-hold1'     // holding SOFFIEREMO VIA
  | 'pun-sliding'   // morph to SOFFIER.EMOVIA
  | 'pun-hold2'     // holding SOFFIER.EMOVIA
  | 'pun-dispersing'// final scatter
  | 'done';

// --------------------------------------------------------------------------
// Deterministic noise helpers
// --------------------------------------------------------------------------

/**
 * Cheap 1D value noise seeded per-integer key.
 * Returns a value in [−1, 1].
 */
function hashNoise(key: number, t: number): number {
  // LCG on the key to get a unique frequency/phase per seed.
  const k = ((key * 1664525 + 1013904223) >>> 0) / 0xffffffff;
  const freq = 0.6 + k * 0.9; // [0.6, 1.5] Hz
  const phase = k * Math.PI * 2;
  return Math.sin(t * freq + phase);
}

/** Noise in x direction for seed i at time t. */
export function noiseX(i: number, t: number): number {
  return hashNoise(i * 2, t);
}

/** Noise in y direction for seed i at time t. */
export function noiseY(i: number, t: number): number {
  return hashNoise(i * 2 + 1, t);
}

// --------------------------------------------------------------------------
// Fillotassi (phyllotaxis) seed placement
// --------------------------------------------------------------------------

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5)); // ≈ 2.399963

/**
 * Generate N seeds arranged on a disc using the golden-angle phyllotaxis
 * spiral, then project the 2D disc position onto a sphere so the seeds
 * are distributed as in nature.
 *
 * Returns world-space (x, y) derived from the sphere projection centred at
 * (cx, cy). The "front" hemisphere is visible; "back" seeds are slightly
 * smaller and behind.
 */
export function createSeeds(cfg: DandelionConfig): Seed[] {
  const { count, cx, cy, radius } = cfg;
  const seeds: Seed[] = new Array(count);

  for (let i = 0; i < count; i++) {
    // Disc position via phyllotaxis.
    const t = i / count;
    const r = Math.sqrt(t) * radius;
    const angle = i * GOLDEN_ANGLE;

    // Azimuth on sphere (theta = angle from y-axis, phi = around z-axis).
    // Map disc r to zenith angle so seeds cover the front hemisphere.
    const phi = angle;
    const theta = (r / radius) * (Math.PI * 0.52); // [0, ~94°] from apex

    // 3D sphere coordinates projected to 2D screen (drop z, keep x/y).
    const sx = Math.sin(theta) * Math.cos(phi);
    const sy = Math.sin(theta) * Math.sin(phi);

    seeds[i] = {
      theta,
      phi,
      x: cx + sx * radius,
      y: cy + sy * radius * 0.55, // flatten y so it reads as a sphere, not disc
      vx: 0,
      vy: 0,
      rot: phi,
      rotSpeed: 0,
      status: 'attached',
      noiseSeed: i,
      target: -1,
      tint: 0,
      stateAge: 0,
      alpha: 1,
    };
  }

  return seeds;
}

// --------------------------------------------------------------------------
// Distacco sopravento
// --------------------------------------------------------------------------

/**
 * Return the "wind exposure" dot product for a seed given the wind direction.
 * Seeds with higher dot product are facing the wind and detach first.
 */
export function windExposure(seed: Seed, wind: WindState): number {
  // The seed's surface normal on the sphere approximated by its (sx, sy)
  // projection from the capolino centre.
  const nx = Math.sin(seed.theta) * Math.cos(seed.phi);
  const ny = Math.sin(seed.theta) * Math.sin(seed.phi);
  const len = Math.hypot(nx, ny) || 1;
  return (nx / len) * wind.dx + (ny / len) * wind.dy;
}

/**
 * Detach seeds that exceed the exposure threshold given current wind strength.
 * Returns the number of newly detached seeds.
 */
export function detachSeeds(
  seeds: Seed[],
  wind: WindState,
  detachThreshold = 0.3,
): number {
  if (wind.strength < 0.05) return 0;

  // Effective threshold: weaker at high wind strength.
  const effectiveThreshold = detachThreshold - wind.strength * 0.35;
  let count = 0;

  for (const seed of seeds) {
    if (seed.status !== 'attached') continue;
    const exposure = windExposure(seed, wind);
    if (exposure > effectiveThreshold) {
      seed.status = 'airborne';
      seed.stateAge = 0;
      // Initial burst velocity from wind.
      seed.vx = wind.dx * wind.strength * (2 + exposure * 3);
      seed.vy = wind.dy * wind.strength * (2 + exposure * 3) - 0.5;
      seed.rotSpeed = (exposure * 0.15 + 0.05) * (seed.noiseSeed % 2 === 0 ? 1 : -1);
      count++;
    }
  }

  return count;
}

// --------------------------------------------------------------------------
// Physics step
// --------------------------------------------------------------------------

const DRAG = 0.88;      // pappo air resistance per frame at 60fps
const GRAVITY = 0.04;   // gentle downward drift
const TURB_SCALE = 0.18; // turbulence force amplitude

/**
 * Integrate one frame for all airborne/forming seeds.
 * dt is elapsed seconds since last frame (ideally ~0.016 at 60fps).
 * t is absolute scene time in seconds (for noise evaluation).
 */
export function stepSeeds(
  seeds: Seed[],
  wind: WindState,
  bounds: { width: number; height: number },
  dt: number,
  t: number,
): void {
  const dtFactor = dt * 60; // scale to 60fps baseline

  for (const seed of seeds) {
    if (seed.status === 'attached' || seed.status === 'gone') continue;

    seed.stateAge += dt;

    if (seed.status === 'airborne') {
      // Turbulence from deterministic noise.
      const tx = noiseX(seed.noiseSeed, t) * TURB_SCALE;
      const ty = noiseY(seed.noiseSeed, t + 100) * TURB_SCALE;

      // Wind force (while blowing).
      const wx = wind.dx * wind.strength * 0.12;
      const wy = wind.dy * wind.strength * 0.12;

      seed.vx += (wx + tx) * dtFactor;
      seed.vy += (wy + ty + GRAVITY) * dtFactor;

      // Drag.
      const d = Math.pow(DRAG, dtFactor);
      seed.vx *= d;
      seed.vy *= d;

      // Clamp velocity.
      const spd = Math.hypot(seed.vx, seed.vy);
      const maxSpd = 8;
      if (spd > maxSpd) {
        seed.vx = (seed.vx / spd) * maxSpd;
        seed.vy = (seed.vy / spd) * maxSpd;
      }

      seed.x += seed.vx * dtFactor;
      seed.y += seed.vy * dtFactor;
      seed.rot += seed.rotSpeed * dtFactor;

      // Mark gone when fully off-screen.
      const margin = 80;
      if (
        seed.x < -margin ||
        seed.x > bounds.width + margin ||
        seed.y < -margin ||
        seed.y > bounds.height + margin
      ) {
        seed.status = 'gone';
        seed.alpha = 0;
      }

    } else if (seed.status === 'forming') {
      _steerToTarget(seed, dt);
    }
  }
}

// --------------------------------------------------------------------------
// Arrival steering (pun moment)
// --------------------------------------------------------------------------

const ARRIVAL_RADIUS = 40; // px — slow-down zone
const MAX_STEER = 3.5;

function _steerToTarget(seed: Seed, dt: number): void {
  if (seed.target < 0) return;
  // Target is stored externally as flat [x,y,x,y,...] — accessed via Soffier.tsx
  // which calls applyTargets() below. This function assumes seed.targetX/Y set.
  const tx = (seed as SeedWithTarget).targetX;
  const ty = (seed as SeedWithTarget).targetY;
  if (tx === undefined || ty === undefined) return;

  const dx = tx - seed.x;
  const dy = ty - seed.y;
  const dist = Math.hypot(dx, dy) || 0.001;

  const desired = dist < ARRIVAL_RADIUS ? (dist / ARRIVAL_RADIUS) * MAX_STEER : MAX_STEER;
  const dxn = (dx / dist) * desired;
  const dyn = (dy / dist) * desired;

  const steerX = dxn - seed.vx;
  const steerY = dyn - seed.vy;
  const slen = Math.hypot(steerX, steerY) || 0.001;
  const maxSteerForce = 0.6;
  const sf = Math.min(slen, maxSteerForce) / slen;

  seed.vx += steerX * sf * dt * 60;
  seed.vy += steerY * sf * dt * 60;

  seed.x += seed.vx * dt * 60;
  seed.y += seed.vy * dt * 60;
  seed.rot += seed.rotSpeed * dt * 60;
  seed.rotSpeed *= 0.95; // dampen rotation as seeds settle

  // Fade in on approach.
  if (seed.alpha < 1) seed.alpha = Math.min(1, seed.alpha + dt * 2);
}

/** Extended seed type used during forming phase — target coords stored inline. */
type SeedWithTarget = Seed & { targetX?: number; targetY?: number };

/**
 * Assign target world-space positions to seeds for the pun moment.
 * `targets` is a flat [x, y, x, y, ...] Float32Array.
 * Seeds that are 'gone' re-enter from the nearest screen edge.
 * Returns seeds that were reassigned so caller can reintroduce 'gone' ones.
 */
export function applyTargets(
  seeds: Seed[],
  targets: Float32Array,
  bounds: { width: number; height: number },
): void {
  const pointCount = targets.length >> 1;
  if (pointCount === 0) return;

  // Separate seeds into "available" (airborne or gone) and "attached".
  const available = seeds.filter(
    (s) => s.status === 'airborne' || s.status === 'gone',
  );

  for (let i = 0; i < available.length && i < pointCount; i++) {
    const seed = available[i] as SeedWithTarget;
    const tx = targets[i * 2];
    const ty = targets[i * 2 + 1];
    seed.target = i;
    seed.targetX = tx;
    seed.targetY = ty;
    seed.status = 'forming';

    // Reintroduce 'gone' seeds from the edge closest to their target.
    if ((seed as Seed).alpha === 0) {
      const { entryX, entryY } = _edgeEntry(tx, ty, bounds);
      seed.x = entryX;
      seed.y = entryY;
      seed.vx = 0;
      seed.vy = 0;
      seed.alpha = 0;
    }
  }
}

/** Retarget forming seeds to new positions (SOFFIEREMO VIA → SOFFIER.EMOVIA). */
export function retargetSeeds(
  seeds: Seed[],
  targets: Float32Array,
  tailMask: Uint8Array,
): void {
  const pointCount = targets.length >> 1;
  const forming = seeds.filter((s) => s.status === 'forming');

  for (let i = 0; i < forming.length && i < pointCount; i++) {
    const seed = forming[i] as SeedWithTarget;
    seed.target = i;
    seed.targetX = targets[i * 2];
    seed.targetY = targets[i * 2 + 1];
    // Tint = 1 if this seed belongs to EMOVIA (tail), else 0.
    seed.tint = tailMask[i] ?? 0;
  }
}

/** Scatter all forming/airborne seeds to disperse definitively. */
export function disperseAll(seeds: Seed[], rng: () => number): void {
  for (const seed of seeds) {
    if (seed.status === 'forming' || seed.status === 'airborne') {
      const angle = rng() * Math.PI * 2;
      const speed = 2 + rng() * 4;
      seed.vx = Math.cos(angle) * speed;
      seed.vy = Math.sin(angle) * speed - 1;
      seed.status = 'airborne';
      seed.target = -1;
      (seed as SeedWithTarget).targetX = undefined;
      (seed as SeedWithTarget).targetY = undefined;
    }
  }
}

// --------------------------------------------------------------------------
// Wind state helpers
// --------------------------------------------------------------------------

/** Grow wind strength on hold (crescendo ~700ms to reach full). */
export function growWind(wind: WindState, dt: number): void {
  wind.strength = Math.min(1, wind.strength + dt * (1 / 0.7));
}

/** Decay wind on release with a short inertia tail. */
export function decayWind(wind: WindState, dt: number): void {
  wind.strength = Math.max(0, wind.strength - dt * 1.2);
}

// --------------------------------------------------------------------------
// Utility
// --------------------------------------------------------------------------

/** Percentage of seeds detached (airborne + forming + gone). */
export function detachedRatio(seeds: Seed[]): number {
  if (seeds.length === 0) return 0;
  const detached = seeds.filter((s) => s.status !== 'attached').length;
  return detached / seeds.length;
}

/**
 * Deterministic LCG RNG — same interface as makeRng from murmuration.ts.
 * Seeded with a constant so tests get reproducible results.
 */
export function makeRng(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return ((s >>> 0) % 100000) / 100000;
  };
}

// --------------------------------------------------------------------------
// Internal: find entry point on screen edge closest to a target.
// --------------------------------------------------------------------------
function _edgeEntry(
  tx: number,
  ty: number,
  bounds: { width: number; height: number },
): { entryX: number; entryY: number } {
  const mx = bounds.width / 2;
  const my = bounds.height / 2;
  // Shoot a ray from the centre outward toward the target, clamp to edge.
  const dx = tx - mx;
  const dy = ty - my;
  const abs = Math.max(Math.abs(dx) / mx, Math.abs(dy) / my) || 1;
  return {
    entryX: mx + (dx / abs) * (mx + 60),
    entryY: my + (dy / abs) * (my + 60),
  };
}
